/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { Layout } from "react-grid-layout";
import { OptionTypeBase } from "./types/common";
import { CSSProperties } from "react";
import { ReactSelectStates } from "./types/components/select";
export const DICOM_DICT_VERSION = 2;
export const REGEX_PASSWORD =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^\p{L}\d\s])[^\s]{8,}$/u;
export const REGEX_EMAIL =
  /^[A-Z0-9.!#$%&'*+-/=?^_`{|}~]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export const REGEX_MOBILE_NUMBER = /^\+[1-9]\d{1,14}([-\s()]*\d+)*$/i;
export const FORMAT_IN_ZONED = "yyyy-MM-dd HH:mm:ssXXX";
export const DATE_NATIVE_FORMAT = "yyyy-MM-dd";
export const URL_REGEX =
  /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/g;

export const DEFAULT_TIMEZONE = {
  value: Intl.DateTimeFormat().resolvedOptions().timeZone,
  label: Intl.DateTimeFormat().resolvedOptions().timeZone,
};
export const DEFAULT_SETTING_AUTO_RELOAD = [5, 10, 15, 30, 60];
export const YYYYMMDD = "YYYYMMDD";
export const yyyyMMdd = "yyyyMMdd";
export const MMddyyyy = "MM/dd/yyyy";
export const ddMMyyyy = "dd/MM/yyyy";
export const yyyyMMddDashed = "yyyy-MM-dd";
export const HHmm = "HH:mm";
export const HHmmss = "HHmmss";
export const MIME_IMAGE = "image/*";
export const MIME_FILES_ACCEPT =
  "text/*,application/pdf,audio/*,video/*,image/*,application/octet-stream,application/json,application/xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/gzip,application/vnd.oasis.opendocument.presentation,application/vnd.oasis.opendocument.spreadsheet,application/vnd.oasis.opendocument.text,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/rtf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const SUPPORT_PORTAL_URL =
  "https://advahealthsolutions.atlassian.net/servicedesk/customer/portal/2";

export enum MoveTrigger {
  STUDY_CREATED = "STUDY_CREATED",
  C_MOVE_COMPLETE = "C_MOVE_COMPLETE",
}

export enum VerificationMode {
  OBJECT_UID_MATCHING = "OBJECT_UID_MATCHING",
  RELATED_INSTANCE_COUNT_MATCHING = "RELATED_INSTANCE_COUNT_MATCHING",
}

export enum Sex {
  A = "A",
  F = "F",
  M = "M",
  N = "N",
  O = "O",
  U = "U",
}

export enum WebhookEventType {
  STUDY_CREATED = "STUDY_CREATED",
  STUDY_UPDATED = "STUDY_UPDATED",
  STUDY_DELETED = "STUDY_DELETED",
  PATIENT_CREATED = "PATIENT_CREATED",
  PATIENT_UPDATED = "PATIENT_UPDATED",
  PATIENT_DELETED = "PATIENT_DELETED",
  ORDER_CREATED = "ORDER_CREATED",
  ORDER_UPDATED = "ORDER_UPDATED",
  REPORT_CREATED = "REPORT_CREATED",
  REPORT_UPDATED = "REPORT_UPDATED",
}

export const WorklistViewer = {
  EUNITY: "eunity",
  MEDDREAM: "meddream",
  ZED: "zed",
  ADVAVIEW: "advaview",
};

export enum ShareStudyMode {
  CREATE_STUDY_LINK = "CREATE_STUDY_LINK",
  SEND_IN_EMAIL = "SEND_IN_EMAIL",
  VIEW_SHARES_LIST = "VIEW_SHARES_LIST",
}

export const MODALITIES = [
  {
    label: "Autorefraction",
    value: "ar",
  },
  {
    label: "Content Assessment Results",
    value: "asmt",
  },
  {
    label: "Audio",
    value: "au",
  },
  {
    label: "Bone Densitometry (Ultrasound)",
    value: "bdus",
  },
  {
    label: "Bone Densitometry (X-Ray)",
    value: "bmd",
  },
  {
    label: "Biomagnetic imaging",
    value: "bi",
  },
  {
    label: "Computed Radiography",
    value: "cr",
  },
  {
    label: "Computed Tomography",
    value: "ct",
  },
  {
    label: "CT Protocol (Performed)",
    value: "ctprotocol",
  },
  {
    label: "Diaphanography",
    value: "dg",
  },
  {
    label: "Document",
    value: "doc",
  },
  {
    label: "Digital Radiography",
    value: "dx",
  },
  {
    label: "Electrocardiography",
    value: "ecg",
  },
  {
    label: "Electroencephalography",
    value: "eeg",
  },
  {
    label: "Electromyography",
    value: "emg",
  },
  {
    label: "Electrooculography",
    value: "eog",
  },
  {
    label: "Cardiac Electrophysiology",
    value: "eps",
  },
  {
    label: "Endoscopy",
    value: "es",
  },
  {
    label: "Fiducials",
    value: "fid",
  },
  {
    label: "General Microscopy",
    value: "gm",
  },
  {
    label: "Hard Copy",
    value: "hc",
  },
  {
    label: "Hemodynamic Waveform",
    value: "hd",
  },
  {
    label: "Intra-oral Radiography",
    value: "io",
  },
  {
    label: "Intraocular Lens Data",
    value: "iol",
  },
  {
    label: "Intravascular Optical Coherence Tomography",
    value: "ivoct",
  },
  {
    label: "Intravascular Ultrasound",
    value: "ivus",
  },
  {
    label: "Keratometry",
    value: "ker",
  },
  {
    label: "value Object Selection",
    value: "ko",
  },
  {
    label: "Lensometry",
    value: "len",
  },
  {
    label: "Laser Surface Scan",
    value: "ls",
  },
  {
    label: "Mammography",
    value: "mg",
  },
  {
    label: "Magnetic Resonance",
    value: "mr",
  },
  {
    label: "Model for 3D Manufacturing",
    value: "m3d",
  },
  {
    label: "Nuclear Medicine",
    value: "nm",
  },
  {
    label: "Ophthalmic Axial Measurements",
    value: "oam",
  },
  {
    label: "Optical Coherence Tomography (non-Ophthalmic)",
    value: "oct",
  },
  {
    label: "Ophthalmic Photography",
    value: "op",
  },
  {
    label: "Ophthalmic Mapping",
    value: "opm",
  },
  {
    label: "Ophthalmic Tomography",
    value: "opt",
  },
  {
    label: "Ophthalmic Tomography B-scan Volume Analysis",
    value: "optbsv",
  },
  {
    label: "Ophthalmic Tomography En Face",
    value: "optenf",
  },
  {
    label: "Ophthalmic Visual Field",
    value: "opv",
  },
  {
    label: "Optical Surface Scan",
    value: "oss",
  },
  {
    label: "Other",
    value: "ot",
  },
  {
    label: "Plan",
    value: "plan",
  },
  {
    label: "Position Sensor",
    value: "pos",
  },
  {
    label: "Presentation State",
    value: "pr",
  },
  {
    label: "Positron Emission tomography",
    value: "pt",
  },
  {
    label: "Panoramic X-Ray",
    value: "px",
  },
  {
    label: "Registration",
    value: "reg",
  },
  {
    label: "Respiratory Waveform",
    value: "resp",
  },
  {
    label: "Radio Fluroscopy",
    value: "rf",
  },
  {
    label: "Radiographic Imaging",
    value: "rg",
  },
  {
    label: "Radiotherapy Image",
    value: "rtimage",
  },
  {
    label: "Radiotherapy Dose",
    value: "rtdose",
  },
  {
    label: "Radiotherapy Structure Set",
    value: "rtstruct",
  },
  {
    label: "Radiotherapy Plan",
    value: "rtplan",
  },
  {
    label: "RT Treatment Record",
    value: "rtrecord",
  },
  {
    label: "Radiotherapy Intent",
    value: "rtintent",
  },
  {
    label: "RT Radiation",
    value: "rtrad",
  },
  {
    label: "Radiotherapy Segment Annotation",
    value: "rtsegann",
  },
  {
    label: "Real World Value Map",
    value: "rwv",
  },
  {
    label: "Segmentation",
    value: "seg",
  },
  {
    label: "Slide Microscopy",
    value: "sm",
  },
  {
    label: "Stereometric Relationship",
    value: "smr",
  },
  {
    label: "SR Document",
    value: "sr",
  },
  {
    label: "Subjective Refraction",
    value: "srf",
  },
  {
    label: "Automated Slide Stainer",
    value: "stain",
  },
  {
    label: "Texture Map",
    value: "texturemap",
  },
  {
    label: "Thermography",
    value: "tg",
  },
  {
    label: "Ultrasound",
    value: "us",
  },
  {
    label: "Visual Acuity",
    value: "va",
  },
  {
    label: "X-Ray Angiography",
    value: "xa",
  },
  {
    label: "External-camera Photography",
    value: "xc",
  },
  {
    label: "Angioscopy",
    value: "as",
  },
  {
    label: "Color flow Doppler",
    value: "cd",
  },
  {
    label: "Cinefluorography",
    value: "cf",
  },
  {
    label: "Culposcopy",
    value: "cp",
  },
  {
    label: "Cystoscopy",
    value: "cs",
  },
  {
    label: "Duplex Doppler",
    value: "dd",
  },
  {
    label: "Digital fluoroscopy",
    value: "df",
  },
  {
    label: "Digital microscopy",
    value: "dm",
  },
  {
    label: "Digital Subtraction Angiography",
    value: "ds",
  },
  {
    label: "Echocardiography",
    value: "ec",
  },
  {
    label: "Fluorescein angiography",
    value: "fa",
  },
  {
    label: "Fundoscopy",
    value: "fs",
  },
  {
    label: "Laparoscopy",
    value: "lp",
  },
  {
    label: "Magnetic resonance angiography",
    value: "ma",
  },
  {
    label: "Magnetic resonance spectroscopy",
    value: "ms",
  },
  {
    label: "Ophthalmic Refraction",
    value: "opr",
  },
  {
    label: "Single-photon Emission Computed Tomography",
    value: "st",
  },
  {
    label: "Videofluorography",
    value: "vf",
  },
];

export const SIDEBAR_STORAGE = {
  SIDEBAR: "sidebar",
  CP_SIDEBAR: "cp_sidebar",
  SECONDARY_SIDEBAR: "secondary_sidebar",
  UNFOLD: "unfold",
  W_SIDEBAR: "worklist_sidebar",
  W_SECONDARY_SIDEBAR: "worklist_secondary_sidebar",
  W_UNFOLD: "worklist_unfold",
};

export enum StorageClass {
  CACHE = "C",
  ONLINE = "O",
  NEARLINE = "N_IA",
  OFFLINE = "OFF",
  ONLINE_INFREQUENT_ACCESS = "O_IA",
}

export enum OperatingSystem {
  WINDOWS = "windows",
  MAC_OS = "mac",
  UNIX = "unix",
  LINUX = "linux",
  DOCKER = "docker",
}

export enum FileType {
  TEXT = "Text",
  PDF = "PDF",
  AUDIO = "Audio",
  VIDEO = "Video",
  IMAGE = "Image",
  HTML = "HTML",
  OTHER = "Other",
}

export const COUNTRY_CODE_ISO = [
  "AFG",
  "ALA",
  "ALB",
  "DZA",
  "ASM",
  "AND",
  "AGO",
  "AIA",
  "ATA",
  "ATG",
  "ARG",
  "ARM",
  "ABW",
  "AUS",
  "AUT",
  "AZE",
  "BHS",
  "BHR",
  "BGD",
  "BRB",
  "BLR",
  "BEL",
  "BLZ",
  "BEN",
  "BMU",
  "BTN",
  "BOL",
  "BES",
  "BIH",
  "BWA",
  "BVT",
  "BRA",
  "IOT",
  "BRN",
  "BGR",
  "BFA",
  "BDI",
  "CPV",
  "KHM",
  "CMR",
  "CAN",
  "CYM",
  "CAF",
  "TCD",
  "CHL",
  "CHN",
  "CXR",
  "CCK",
  "COL",
  "COM",
  "COG",
  "COD",
  "COK",
  "CRI",
  "CIV",
  "HRV",
  "CUB",
  "CUW",
  "CYP",
  "CZE",
  "DNK",
  "DJI",
  "DMA",
  "DOM",
  "ECU",
  "EGY",
  "SLV",
  "GNQ",
  "ERI",
  "EST",
  "SWZ",
  "ETH",
  "FLK",
  "FRO",
  "FJI",
  "FIN",
  "FRA",
  "GUF",
  "PYF",
  "ATF",
  "GAB",
  "GMB",
  "GEO",
  "DEU",
  "GHA",
  "GIB",
  "GRC",
  "GRL",
  "GRD",
  "GLP",
  "GUM",
  "GTM",
  "GGY",
  "GIN",
  "GNB",
  "GUY",
  "HTI",
  "HMD",
  "VAT",
  "HND",
  "HKG",
  "HUN",
  "ISL",
  "IND",
  "IDN",
  "IRN",
  "IRQ",
  "IRL",
  "IMN",
  "ISR",
  "ITA",
  "JAM",
  "JPN",
  "JEY",
  "JOR",
  "KAZ",
  "KEN",
  "KIR",
  "PRK",
  "KOR",
  "KWT",
  "KGZ",
  "LAO",
  "LVA",
  "LBN",
  "LSO",
  "LBR",
  "LBY",
  "LIE",
  "LTU",
  "LUX",
  "MAC",
  "MDG",
  "MWI",
  "MYS",
  "MDV",
  "MLI",
  "MLT",
  "MHL",
  "MTQ",
  "MRT",
  "MUS",
  "MYT",
  "MEX",
  "FSM",
  "MDA",
  "MCO",
  "MNG",
  "MNE",
  "MSR",
  "MAR",
  "MOZ",
  "MMR",
  "NAM",
  "NRU",
  "NPL",
  "NLD",
  "NCL",
  "NZL",
  "NIC",
  "NER",
  "NGA",
  "NIU",
  "NFK",
  "MKD",
  "MNP",
  "NOR",
  "OMN",
  "PAK",
  "PLW",
  "PSE",
  "PAN",
  "PNG",
  "PRY",
  "PER",
  "PHL",
  "PCN",
  "POL",
  "PRT",
  "PRI",
  "QAT",
  "REU",
  "ROU",
  "RUS",
  "RWA",
  "BLM",
  "SHN",
  "KNA",
  "LCA",
  "MAF",
  "SPM",
  "VCT",
  "WSM",
  "SMR",
  "STP",
  "SAU",
  "SEN",
  "SRB",
  "SYC",
  "SLE",
  "SGP",
];

export enum DAYS {
  DAY = "DAY",
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

export enum SLAType {
  TIME = "TIME",
  NEXT = "NEXT",
}

export enum ApplicationEntityType {
  GATEWAY = "GATEWAY",
  CLOUD = "CLOUD",
}

export enum UploadMode {
  ASYNCHRONOUS = "ASYNCHRONOUS",
  SYNCHRONOUS = "SYNCHRONOUS",
}

export enum CMoveMode {
  ASYNCHRONOUS = "ASYNCHRONOUS",
  SYNCHRONOUS = "SYNCHRONOUS",
}

export enum DayTimeOfWeeksType {
  DAY_TIME = "DAY_TIME",
  QUERY_GRANULARITY = "QUERY_GRANULARITY",
}

export const PERMISSIONS = {
  VIEW_NOTE: "viewNote",
  MANAGE_OWN_NOTE: "manageOwnNote",
  MANAGE_ALL_NOTES: "manageAllNotes",
  DELETE_OWN_NOTE: "deleteOwnNote",
  DELETE_ALL_NOTES: "deleteAllNotes",
  MANAGE_ORDER_ASSIGNMENT: "manageOrderAssignment",
  RUSH_ORDER: "rushOrder",
  MANAGE_DUE_TIME: "manageDueTime",
  ACCESS_ALL_STUDIES: "accessAllStudies",
  ACCESS_REFERRED_STUDIES: "accessReferredStudies",
  VIEW_STUDY: "viewStudy",
  DOWNLOAD_STUDY: "downloadStudy",
  UPLOAD_STUDY: "uploadStudy",
  MOVE_STUDY: "moveStudy",
  DELETE_STUDY: "deleteStudy",
  MANAGE_CODE_MANAGEMENT: "manageCodeManagement",
  VIEW_CODE_MANAGEMENT: "viewCodeManagement",
  SHARE_STUDY: "shareStudy",
  CHANGE_STUDY_AVAILABILITY: "changeStudyAvailability",
  VIEW_PRACTITIONER_MANAGEMENT: "viewPractitionerManagement",
  MANAGE_PRACTITIONER_MANAGEMENT: "managePractitionerManagement",
  EDIT_STUDY: "editStudy",
  VIEW_STUDY_REPORT: "viewStudyReport",
  VIEW_STUDY_IMAGE: "viewStudyImage",
  AUDIT_STUDY: "auditStudy",
  QUERY_RETRIEVE: "queryRetrieve",
  VIEW_STUDY_REQUIRING_VALIDATION: "viewStudyRequiringValidation",
  VALIDATE_STUDY_REQUIRING_VALIDATION: "validateStudyRequiringValidation",
  DELETE_STUDY_REQUIRING_VALIDATION: "deleteStudyRequiringValidation",
  VIEW_INCOMING_QUEUE: "viewIncomingQueue",
  IMPERSONATE_USER: "impersonateUser",
  VIEW_UPLOAD_QUEUE: "viewUploadQueue",
  MANAGE_UPLOAD_QUEUE: "manageUploadQueue",
  VIEW_ASSOCIATION_MONITOR: "viewAssociationMonitor",
  VIEW_ROUTING_QUEUE: "viewRoutingQueue",
  MAGE_ROUTING_QUEUE: "manageRoutingQueue",
  VIEW_GATEWAY: "viewGateway",
  VIEW_GATEWAY_LOG: "viewGatewayLog",
  MANAGE_GATEWAY: "manageGateway",
  REGENERATE_GATEWAY_AUTHENTICATION_KEY: "regenerateGatewayAuthenticationKey",
  VIEW_ORGANISATION: "viewOrganisation",
  MANAGE_ORGANISATION: "manageOrganisation",
  VIEW_LOCAL_AE: "viewLocalApplicationEntity",
  MANAGE_LOCAL_AE: "manageLocalApplicationEntity",
  VIEW_REMOTE_AE: "viewRemoteApplicationEntity",
  MANAGE_REMOTE_AE: "manageRemoteApplicationEntity",
  MANAGE_GENERAL_SETTING: "manageGeneralSetting",
  MANAGE_API_KEY: "manageApiKey",
  VIEW_GENERAL_SETTING: "viewGeneralSetting",
  MANAGE_LATENCY_CACHE_SETTING: "manageLatencyCacheSetting",
  MANAGE_SECURITY: "manageSecurity",
  MANAGE_AUTHENTICATION: "manageAuthentication",
  VIEW_AUTHENTICATION: "viewAuthentication",
  MANAGE_ASSIGNING_AUTHORITY: "manageAssigningAuthority",
  MANAGE_FEATURE_VISIBILITY: "manageFeatureVisibility",
  VIEW_USER: "viewUser",
  MANAGE_USER: "manageUser",
  VIEW_ROLE: "viewRole",
  MANAGE_ROLE: "manageRole",
  VIEW_AUDIT_TOOL: "viewAuditTool",
  VIEW_PAYMENT_METHOD: "viewPaymentMethod",
  MANAGE_PAYMENT_METHOD: "managePaymentMethod",
  VIEW_BILL: "viewBill",
  VIEW_BILLING_DETAIL: "viewBillingDetail",
  MANAGE_BILLING_DETAIL: "manageBillingDetail",
  VIEW_BILLING_ALERT: "viewBillingAlert",
  MANAGE_BILLING_ALERT: "manageBillingAlert",
  VIEW_PAYMENT: "viewPayment",
  PAY_INVOICE: "payInvoice",
  VIEW_HL7_DATA_MAPPING: "viewHL7DataMapping",
  MANAGE_HL7_DATA_MAPPING: "manageHL7DataMapping",
  VIEW_UPLOAD_RULE: "viewUploadRule",
  MANAGE_UPLOAD_RULE: "manageUploadRule",
  VIEW_COMPRESSION_RULE: "viewCompressionRule",
  MANAGE_COMPRESSION_RULE: "manageCompressionRule",
  VIEW_HL7_SERVICE: "viewHL7Service",
  MANAGE_HL7_SERVICE: "manageHL7Service",
  VIEW_ROUTING_RULE: "viewRoutingRule",
  MANAGE_ROUTING_RULE: "manageRoutingRule",
  VIEW_HL7_MESSAGE_LOG: "viewHL7MessageLog",
  MANAGE_HL7_MESSAGE_LOG: "manageHL7MessageLog",
  VIEW_TRANSFORM_RULE: "viewTransformRule",
  MANAGE_TRANSFORM_RULE: "manageTransformRule",
  ACCESS_MEDDREAM: "accessMedDream",
  ACCESS_WEASIS: "accessWeasis",
  ACCESS_EUNITY: "accessEUnity",
  UPLOAD_DOCUMENT: "uploadDocument",
  MANAGE_DOCUMENT: "manageDocument",
  VIEW_DOCUMENT: "viewDocument",
  VIEW_PREFETCHING_RULE: "viewPrefetchingRule",
  MANAGE_PREFETCHING_RULE: "managePrefetchingRule",
  VIEW_PATIENT: "viewPatient",
  MANAGE_PATIENT: "managePatient",
  VIEW_AUDIT_LOG: "viewAuditLog",
  VIEW_RETRIEVE_QUEUE: "viewMoveQueue",
  MANAGE_RETRIEVE_QUEUE: "manageMoveQueue",
  ACCESS_ZED_PORTAL: "accessZedPortal",
  MANAGE_MIGRATION_JOB: "manageMigrationJob",
  VIEW_MIGRATION_JOB: "viewMigrationJob",
  VIEW_REPORT: "viewReport",
  MANAGE_REPORT: "manageReport",
  VIEW_CUSTOMERPACS: "viewCustomerPACS",
  MANGE_CUSTOMERPACS: "manageCustomerPACS",
  MANAGE_CLINICIAN_PORTAL: "manageClinicianPortal",
  VIEW_CODE_SYSTEM: "viewCodeSystem",
  MANAGE_CODE_SYSTEM: "manageCodeSystem",
  VIEW_DIRECTORY: "viewDirectory",
  MANAGE_DIRECTORY: "manageDirectory",
  BREAK_GLASS_ACCESS: "breakGlassAccess",
  VIEW_STUDY_TAG_RULE: "viewStudyTagRule",
  MANAGE_STUDY_TAG_RULE: "manageStudyTagRule",
  VIEW_SENSITIVE_STUDY: "viewSensitiveStudy",
  VIEW_SENSITIVE_STUDY_PATIENT_VERIFY: "viewSensitiveStudyPatientVerify",
  MANAGE_SENSITIVE_STUDY: "manageSensitiveStudy",
  ACCESS_CHAT: "accessChat",
  VIEW_ORDER: "viewOrder",
  MANAGE_ORDER: "manageOrder",
  VIEW_ILM_MONITOR: "viewILMMonitor",
  EXPLICIT_ACCESS_GRANT: "explicitAccessGrant",
  VIEW_SENSITIVE_DOCUMENT: "viewSensitiveDocument",
  MANAGE_SENSITIVE_DOCUMENT: "manageSensitiveDocument",
  MANAGE_STUDY_STORAGE_CLASS: "manageStudyStorageClass",
  // TODO WORKLIST
  ACCESS_WORKLIST: "accessWorklist",
  VIEW_CONCURRENCY_LOCKS: "viewConcurrencyLocks",
  MANAGE_CONCURRENCY_LOCKS: "manageConcurrencyLocks",
  VIEW_REPORT_TEMPLATES: "viewReportTemplates",
  VIEW_REPORT_PRIORITY: "viewReportPriority",
  MANAGE_PEER_REVIEW_CONFIGURATION: "managePeerReviewConfiguration",
  VIEW_PEER_REVIEW_CONFIGURATION: "viewPeerReviewConfiguration",
  MANAGE_REPORT_PRIORITY: "manageReportPriority",
  MANAGE_REPORT_TEMPLATES: "manageReportTemplates",
  MANAGE_BOARDS: "manageBoards",
  VIEW_BOARDS: "viewBoards",
  MANAGE_CRITICAL_RESULT: "manageCriticalResult",
  VIEW_CRITICAL_RESULT: "viewCriticalResult",
  MANAGE_CRITICAL_RESULT_CONFIG: "manageCriticalResultConfig",
  VIEW_CRITICAL_RESULT_CONFIG: "viewCriticalResultConfig",
  MANAGE_INCOMPLETE_CONFIG: "manageIncompleteConfig",
  VIEW_INCOMPLETE_CONFIG: "viewIncompleteConfig",
  MANAGE_INCOMPLETE: "manageIncomplete",
  VIEW_INCOMPLETE: "viewIncomplete",
  VIEW_DATA_SETS: "viewDataSet",
  MANAGE_DATA_SETS: "manageDataSet",
  MANAGE_DISTRIBUTION_RULE: "manageDistributionRule",
  VIEW_DISTRIBUTION_RULE: "viewDistributionRule",
  VIEW_DATA_SOURCE: "viewDataSource",
  MANAGE_DATA_SOURCE: "manageDataSource",
  VIEW_IMMUTABLE_STORAGE_RULE: "viewImmutableStorageRule",
  MANAGE_IMMUTABLE_STORAGE_RULE: "manageImmutableStorageRule",
  MANAGE_SLA: "manageSla",
  VIEW_SLA: "viewSla",
  MANAGE_SUBSPECIALITY: "manageSubspeciality",
  VIEW_SUBSPECIALITY: "viewSubspeciality",
  MANAGE_WORKLIST_FILTER: "manageWorklistFilter",
  VIEW_WORKLIST_FILTER: "viewWorklistFilter",
  // portal landing
  MANAGE_PATIENT_PORTAL: "managePatientPortal",
  MANAGE_TECH_PORTAL: "manageTechPortal",
  // Tasks
  MANAGE_TASK_CONFIGURATION: "manageTaskConfiguration",
  VIEW_TASK_CONFIGURATION: "viewTaskConfiguration",
  // Webhooks
  MANAGE_WEBHOOK: "manageWebhook",
  VIEW_WEBHOOK: "viewWebhook",
  // Upload Links
  MANAGE_UPLOAD_LINK: "manageUploadLink",
  VIEW_UPLOAD_LINK: "viewUploadLink",
  VIEW_OWN_TASK: "viewOwnTask",
  VIEW_ALL_TASK: "viewAllTasks",
  MANAGE_OWN_TASK: "manageOwnTask",
  MANAGE_ALL_TASK: "manageAllTasks",
  DELETE_OWN_TASK: "deleteOwnTask",
  DELETE_ALL_TASK: "deleteAllTasks",
  MANAGE_USER_GROUP: "manageUserGroup",
  VIEW_USER_GROUP: "viewUserGroup",
  VIEW_QUALIFICATION: "viewQualification",
  MANAGE_QUALIFICATION: "manageQualification",
  // peer review
  PEER_REVIEW_MONITOR: "peerReviewMonitor",
  PEER_REVIEW_QUEUE: "peerReviewQueue",
  PEER_REVIEW_FEEDBACK: "peerReviewFeedback",
  MANAGE_ID_SEQUENCE: "manageIdSequence",
  VIEW_ID_SEQUENCE: "viewIdSequence",
  // Custom Field
  VIEW_CUSTOM_FIELD_MANAGEMENT: "viewCustomField",
  MANAGE_CUSTOM_FIELD_MANAGEMENT: "manageCustomField",
  MANAGE_EXTERNAL_APPS: "manageExternalApps",
  VIEW_EXTERNAL_APPS: "viewExternalApps",
  MANAGE_REPORT_SETTING: "manageReportSettings",
  VIEW_REPORT_SETTING: "viewReportSettings",
  MANAGE_ALL_VIEWER_SETTINGS: "manageAllViewerSettings",
};

export enum ReportStatusEnum {
  REGISTERED_O = "registered_o",
  REGISTERED_I = "registered_i",
  REGISTERED_S = "registered_s",
  PARTIAL = "partial",
  PRELIMINARY = "preliminary",
  FINAL = "final",
  AMENDED = "amended",
  MODIFIED = "modified",
  CORRECTED = "corrected",
  APPENDED = "appended",
  CANCELLED = "cancelled",
  ENTERED_IN_ERROR = "entered_in_error",
  UNKNOWN = "unknown",
}

export enum FhirReportStatusEnum {
  REGISTERED = "registered",
  PARTIAL = "partial",
  PRELIMINARY = "preliminary",
  MODIFIED = "modified",
  FINAL = "final",
  AMENDED = "amended",
  CORRECTED = "corrected",
  APPENDED = "appended",
  CANCELLED = "cancelled",
  ENTERED_IN_ERROR = "entered_in_error",
  UNKNOWN = "unknown",
}

export enum DateRangePresets {
  TODAY = "TODAY",
  YESTERDAY = "YESTERDAY",
  THIS_WEEK = "THIS_WEEK",
  LAST_WEEK = "LAST_WEEK",
  THIS_MONTH = "THIS_MONTH",
  LAST_MONTH = "LAST_MONTH",
}

export enum Region {
  SGP = "sgp",
  HKG = "HKG",
}

export enum OrderStatus {
  SOME_RESULTS_AVAILABLE = "SOME_RESULTS_AVAILABLE",
  CANCELLED = "CANCELLED",
  COMPLETE = "COMPLETE",
  DISCONTINUED = "DISCONTINUED",
  ORDER_NOT_FOUND = "ORDER_NOT_FOUND",
  ON_HOLD = "ON_HOLD",
  IN_PROCESS = "IN_PROCESS",
  REPLACED = "REPLACED",
  SCHEDULED = "SCHEDULED",
}

export enum CustomerPACSStatus {
  ACCEPTED = "A",
  DECLINED = "D",
  PENDING = "P",
  REVOKED = "R",
}

export enum DiagnosticReportStatusEnum {
  PRELIMINARY = "PRELIMINARY",
  FINAL = "FINAL",
  AMENDED = "AMENDED",
}

export enum WorklistReportStatus {
  NOT_READY = "NOT_READY",
  IN_COMPLETE = "IN_COMPLETE",
  READY_TO_REPORT = "READY_TO_REPORT",
  DICATED = "DICATED",
  TYPED = "TYPED",
  VERIFIED = "VERIFIED",
  DISTRIBUTED = "DISTRIBUTED",
}

export enum PresenceStatusEnum {
  ONLINE = "O",
  AWAY = "AWAY",
  OFFLINE = "OFF",
}

export enum MessageTypeEnum {
  MESSAGE = "M",
  STUDY = "S",
}

export enum FHIRSystemType {
  OID = "OID",
  UUID = "UUID",
  URI = "URI",
  IRI_STEM = "IRI_STEM",
  V2CSMNEMONIC = "V2CSMNEMONIC",
  OTHER = "OTHER",
}

export const DELAY_TIME_SEND_WSS = 60 * 1000; // 1mn
export const DELAY_TIME_LOCK_SCREENS = 5 * DELAY_TIME_SEND_WSS; // 10mn

export const DemographicMismatchBehavior = {
  strict: "STRICT",
  relaxed: "RELAXED",
  ignore: "IGNORE",
};

export const EditorTheme = {
  ltr: "ltr",
  rtl: "rtl",
  placeholder: "editor-placeholder",
  paragraph: "editor-paragraph",
  quote: "editor-quote",
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
    h3: "editor-heading-h3",
    h4: "editor-heading-h4",
    h5: "editor-heading-h5",
  },
  list: {
    nested: {
      listitem: "editor-nested-listitem",
    },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listitem",
  },
  image: "editor-image",
  link: "editor-link",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    overflowed: "editor-text-overflowed",
    hashtag: "editor-text-hashtag",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
    underlineStrikethrough: "editor-text-underlineStrikethrough",
    code: "editor-text-code",
  },
  table: "PlaygroundEditorTheme__table",
  tableAddColumns: "PlaygroundEditorTheme__tableAddColumns",
  tableAddRows: "PlaygroundEditorTheme__tableAddRows",
  tableCell: "PlaygroundEditorTheme__tableCell",
  tableCellActionButton: "PlaygroundEditorTheme__tableCellActionButton",
  tableCellActionButtonContainer:
    "PlaygroundEditorTheme__tableCellActionButtonContainer",
  tableCellEditing: "PlaygroundEditorTheme__tableCellEditing",
  tableCellHeader: "PlaygroundEditorTheme__tableCellHeader",
  tableCellPrimarySelected: "PlaygroundEditorTheme__tableCellPrimarySelected",
  tableCellResizer: "PlaygroundEditorTheme__tableCellResizer",
  tableCellSelected: "PlaygroundEditorTheme__tableCellSelected",
  tableCellSortedIndicator: "PlaygroundEditorTheme__tableCellSortedIndicator",
  tableResizeRuler: "PlaygroundEditorTheme__tableCellResizeRuler",
  tableSelected: "PlaygroundEditorTheme__tableSelected",
  code: "editor-code",
  codeHighlight: {
    atrule: "editor-tokenAttr",
    attr: "editor-tokenAttr",
    boolean: "editor-tokenProperty",
    builtin: "editor-tokenSelector",
    cdata: "editor-tokenComment",
    char: "editor-tokenSelector",
    class: "editor-tokenFunction",
    "class-name": "editor-tokenFunction",
    comment: "editor-tokenComment",
    constant: "editor-tokenProperty",
    deleted: "editor-tokenProperty",
    doctype: "editor-tokenComment",
    entity: "editor-tokenOperator",
    function: "editor-tokenFunction",
    important: "editor-tokenVariable",
    inserted: "editor-tokenSelector",
    keyword: "editor-tokenAttr",
    namespace: "editor-tokenVariable",
    number: "editor-tokenProperty",
    operator: "editor-tokenOperator",
    prolog: "editor-tokenComment",
    property: "editor-tokenProperty",
    punctuation: "editor-tokenPunctuation",
    regex: "editor-tokenVariable",
    selector: "editor-tokenSelector",
    string: "editor-tokenSelector",
    symbol: "editor-tokenProperty",
    tag: "editor-tokenProperty",
    url: "editor-tokenOperator",
    variable: "editor-tokenVariable",
  },
};

export enum LayoutLocalStorage {
  LANDSCAPE = "layout_landscape",
  PORTRAIT = "layout_portrait",
  DICTATION_LANDSCAPE = "dictation_layout_landscape",
  DICTATION_PORTRAIT = "dictation_layout_portrait",
  RADPAIR_LANDSCAPE = "radpair_layout_landscape",
  RADPAIR_PORTRAIT = "radpair_layout_portrait",
  TRANSCRIPTION_LANDSCAPE = "trans_layout_landscape",
  TRANSCRIPTION_PORTRAIT = "trans_layout_portrait",
}

export enum LayoutItem {
  LEFT = "LEFT",
  VIEWER = "VIEWER",
  EDITOR = "EDITOR",
  REPORT = "REPORT",
  REVIEW_RESULT = "REVIEW_RESULT",
  RIGHT = "RIGHT",
  MID = "MID",
}

// layout constants.
export const RadpairLayoutLandscape: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 7,
    h: 20,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    maxW: 9,
    minW: 3,
  },
  {
    i: LayoutItem.EDITOR,
    x: 7,
    y: 0,
    w: 5,
    h: 20,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    minW: 3,
  },
];
export const PeerReviewMonitorLayoutLandscape: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 6,
    h: 20,
    isResizable: false,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    maxW: 9,
    minW: 5,
  },
  {
    i: LayoutItem.REPORT,
    x: 6,
    y: 0,
    w: 3,
    h: 20,
    isResizable: false,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    minW: 3,
  },
  {
    i: LayoutItem.REVIEW_RESULT,
    x: 9,
    y: 0,
    w: 3,
    h: 20,
    isResizable: false,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    minW: 3,
  },
];
export const RadpairLayoutPortrait: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 12,
    h: 25,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
  },
  {
    i: LayoutItem.EDITOR,
    x: 0,
    y: 25,
    w: 12,
    h: 25,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    minW: 12,
    maxW: 12,
  },
];
export const TypingLayoutLandscape: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 7,
    h: 20,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    maxW: 9,
    minW: 3,
  },
  {
    i: LayoutItem.EDITOR,
    x: 7,
    y: 0,
    w: 5,
    h: 20,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    minW: 3,
    maxW: 9,
  },
];
export const TypingLayoutPortrait: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 12,
    h: 25,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    minW: 12,
    maxW: 12,
  },
  {
    i: LayoutItem.EDITOR,
    x: 0,
    y: 25,
    w: 12,
    h: 25,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    minW: 12,
    maxW: 12,
  },
];
export const DictationLayoutLandscape: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 12,
    h: 20,
    isResizable: false,
    isDraggable: false,
    isBounded: true,
  },
];
export const DictationLayoutPortrait: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 12,
    h: 40,
    isResizable: false,
    isDraggable: false,
    isBounded: true,
  },
];
export const TranscriptionLayoutLandscape: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 7,
    h: 20,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    maxW: 9,
    minW: 3,
  },
  {
    i: LayoutItem.EDITOR,
    x: 7,
    y: 0,
    w: 5,
    h: 20,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    maxH: 20,
    minW: 3,
  },
];
export const TranscriptionLayoutPortrait: Layout[] = [
  {
    i: LayoutItem.LEFT,
    x: 0,
    y: 0,
    w: 12,
    h: 25,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
  },
  {
    i: LayoutItem.EDITOR,
    x: 0,
    y: 25,
    w: 12,
    h: 25,
    isResizable: true,
    isDraggable: false,
    isBounded: true,
    minW: 12,
    maxW: 12,
  },
];

export enum ContentType {
  APPLICATION_PDF = "application/pdf",
  TEXT_PLAIN = "text/plain",
  PDF_TAG = "pdf",
  DATA_APPLICATION_PDF = "data:application/pdf;",
  DATA_TEXT_HTML = "data:text/html;",
  DATA_TEXT_XML = "data:text/xml;",
  TEXT = "TEXT",
  HTML = "HTML",
  TEXT_HTML = "text/html",
  IMAGE_JPG = "image/jpeg",
  IMAGE_PNG = "image/png",
  IMG_JPG_S = "image/jpg",
  IMG_PNG_S = "img/png",
}

export const OrderResizeObject = {
  patientName: 200,
  patientId: 170,
  description: 200,
  accessionNumber: 150,
  modality: 50,
  occurrenceAt: 170,
  status: 200,
  procedurePriority: 120,
  reportPriority: 120,
};

export const StudiesResizeObject = {
  patientName: 200,
  patientId: 170,
  tasks: 50,
  dob: 100,
  accessionNumber: 150,
  description: 200,
  date: 170,
  modalities: 50,
  si: 70,
  referrer: 100,
  orderingProviderName: 100,
  dataSet: 100,
  storageClass: 70,
  stage: 70,
  validation: 70,
};

export const PeerReviewQueueObject = {
  description: 200,
  modality: 80,
  studyDate: 120,
  readyForReviewAt: 120,
  status: 120,
  finalScore: 100,
};

export const PeerReviewFeedbackObject = {
  pid: 170,
  patient: 130,
  description: 200,
  modality: 80,
  accessionNumber: 140,
  studyDate: 120,
  reviewClosedAt: 120,
  finalScore: 100,
};

export const PeerReviewMonitorObject = {
  pid: 170,
  patient: 130,
  description: 200,
  modality: 80,
  accessionNumber: 140,
  studyDate: 120,
  reportingBy: 120,
  reviewedAt: 120,
  readyForReviewAt: 120,
  reviewClosedAt: 120,
  finalScore: 120,
};

export const WorklistResizeObject = {
  pid: 170,
  patient: 130,
  description: 200,
  modality: 80,
  occurrenceAt: 120,
  accessionNumber: 140,
  worklistStatus: 120,
  imageStatus: 70,
  referrer: 110,
  orderingProvider: 100,
  assignedUser: 120,
  reportPriority: 110,
  procedurePriority: 120,
  dueIn: 120,
  organisation: 90,
  site: 70,
  patientClass: 70,
  verifiedFirstAt: 120,
  verifiedLastAt: 120,
};

export enum Action {
  CREATE,
  UPDATE,
  DELETE,
  VIEW,
}

export enum DayOfWeeks {
  MON = "MONDAY",
  TUE = "TUESDAY",
  WED = "WEDNESDAY",
  THU = "THURSDAY",
  FRI = "FRIDAY",
  SAT = "SATURDAY",
  SUN = "SUNDAY",
}

export enum Frequency {
  FIRST = "FIRST",
  SECOND = "SECOND",
  THIRD = "THIRD",
  FOURTH = "FOURTH",
  LAST = "LAST",
}

export enum DataSourceType {
  DICOM_WEB = "DICOM_WEB",
}

export const EQUAL = "EQUAL";
export const NOT_EQUAL = "NOT_EQUAL";
export const BETWEEN = "BETWEEN";
export const NOT_BETWEEN = "NOT_BETWEEN";
export const EMPTY = "EMPTY";
export const EXISTS = "EXISTS";
export const GREATER = "GREATER";
export const GREATER_OR_EQUAL = "GREATER_OR_EQUAL";
export const HL7 = "HL7";
export const IN_RANGE = "IN_RANGE";
export const LESS = "LESS";
export const LESS_OR_EQUAL = "LESS_OR_EQUAL";
export const LIKE = "LIKE";
export const NOT_EMPTY = "NOT_EMPTY";
export const NOT_EXISTS = "NOT_EXISTS";
export const NOT_LIKE = "NOT_LIKE";
export const OUT_OF_RANGE = "OUT_OF_RANGE";
export const DICOM = "DICOM";
export const DAY_OF_WEEK = "DAY_OF_WEEK";
export const TIME_OF_DAY = "TIME_OF_DAY";
export const SCRIPT = "SCRIPT";
export const CONTAINS = "CONTAINS";
export const NOT_CONTAINS = "NOT_CONTAINS";
export const IN = "IN";
export const NOT_IN = "NOT_IN";
export const DYNAMIC = "DYNAMIC";
export const AI_RELEVANCY = "AI_RELEVANCY";
export const DATE_INTERVAL = "DATE_INTERVAL";
export const TIME_INTERVAL = "TIME_INTERVAL";
export const DATE_AND_TIME_INTERVAL = "DATE_AND_TIME_INTERVAL";
export const BETWEEN_DATE_INTERVAL = "BETWEEN_DATE_INTERVAL";
export const BETWEEN_TIME_INTERVAL = "BETWEEN_TIME_INTERVAL";
export const BETWEEN_DATE_AND_TIME_INTERVAL = "BETWEEN_DATE_AND_TIME_INTERVAL";
export const CONSTANT = "CONSTANT";
export const TIME = "TIME";
export const DATE_RANGE = "DATE_RANGE";
export const ANONYMIZE = "ANONYMIZE";
export const TIME_RANGE = "TIME_RANGE";
export const DATE_AND_TIME = "DATE_AND_TIME";
export const DATE_AND_TIME_RANGE = "DATE_AND_TIME_RANGE";
export const DATE = "DATE";
export const REGEX = "REGEX";
export const REGEX_PATH = "REGEX_PATH";
export const COPY = "COPY";
export const REMOVE = "REMOVE";
export const CURRENT_DATE = "CURRENT_DATE";
export const DICOM_INPUT = "DICOM_INPUT";
export const MODALITY = "MODALITY";
export const BODY_PART = "BODY_PART";
export const EXAM_CODE = "EXAM_CODE";
export const AGE_RANGE = "AGE_RANGE";
export const GENDER = "GENDER";
export const STUDY_DESCRIPTION = "STUDY_DESCRIPTION";
export const ORGANISATION = "ORGANISATION";
export const SITE = "SITE";
export const REPORTING_PRIORITY = "REPORTING_PRIORITY";
export const PATIENT_CLASS = "PATIENT_CLASS";

export const HL7_FIELD_FORMAT =
  /^(\/\.)?[A-Z\d]{3}-\d{1,3}(\(\d+\))?-\d{1,3}(-\d{1,3})?$/i;

export const DICOM_TAG_FORMAT = /^\([\da-f]{4},[\da-f]{4}\)(\[([\d]*)?\])?$/i;

export const DYNAMIC_DURATION_PATTERN =
  /^([-+]?)P(?:([-+]?[0-9]+)D)?(T(?:([-+]?[0-9]+)H)?(?:([-+]?[0-9]+)M)?(?:([-+]?[0-9]+)(?:[.,]([0-9]{0,9}))?S)?)?$/i;

export const DYNAMIC_PERIOD_PATTERN =
  /^([-+]?)P(?:([-+]?[0-9]+)Y)?(?:([-+]?[0-9]+)M)?(?:([-+]?[0-9]+)W)?(?:([-+]?[0-9]+)D)?$/i;

// Letters and digits, single hyphens between them
export const PATH_NAME_PATTERN = /^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/;

export const DYNAMIC_FORMAT_DATE_AND_TIME =
  /^([-+]?)P(?:([-+]?[0-9]+)Y)?(?:([-+]?[0-9]+)M)?(?:([-+]?[0-9]+)W)?(?:([-+]?[0-9]+)D)?(?:T(?:([-+]?[0-9]+)H)?(?:([-+]?[0-9]+)M)?(?:([-+]?[0-9]+)(?:[.,]([0-9]{0,9}))?S)?)?$/i;

export const REACT_SELECT_COLORS = {
  // shared
  FOCUS_BORDER: "#8963F6",
  WHITE: "#FFFFFF",
  DISABLED: "#f4f4f4",
  MULTI_VALUE_BG: "#D6D6E5",
  // light mode
  LIGHT_MENU_BG: "#F2F2F9",
  LIGHT_OPTION_BG: "#FFFFFF",
  LIGHT_OPTION_FOCUSED_BG: "#ECECF4",
  LIGHT_OPTION_SELECTED_BG: "#6A39CB",
  LIGHT_TEXT: "#20202B",
  // dark mode
  DARK_BORDER: "#3B3B4F",
  DARK_MENU_BG: "#3B3B4F",
  DARK_OPTION_BG: "#232331",
  DARK_OPTION_FOCUSED_BG: "#3B3B4F",
  DARK_OPTION_SELECTED_BG: "#8963F6",
  // site group heading
  GROUP_HEADING_BG: "#A2A5FF",
  GROUP_HEADING_TEXT: "#333333",
};

export const SELECT_DARK_STYLES = {
  placeholder: (base: CSSProperties) => ({
    ...base,
    opacity: 1,
  }),
  control: (base: CSSProperties, state: ReactSelectStates) => {
    return {
      ...base,
      backgroundColor: "transparent", // dark color.
      borderColor: state.isFocused
        ? REACT_SELECT_COLORS.FOCUS_BORDER
        : REACT_SELECT_COLORS.DARK_BORDER,
      boxShadow: state.isFocused
        ? `0 0 0 1px ${REACT_SELECT_COLORS.FOCUS_BORDER}`
        : "none",
      "&:hover": {
        borderColor: state.isFocused
          ? REACT_SELECT_COLORS.FOCUS_BORDER
          : REACT_SELECT_COLORS.DARK_BORDER,
      },
    };
  },
  menu: (base: CSSProperties) => ({
    ...base,
    backgroundColor: REACT_SELECT_COLORS.DARK_MENU_BG,
    marginTop: 1,
  }),
  option: (base: CSSProperties, state: ReactSelectStates) => ({
    ...base,
    backgroundColor: state.isSelected
      ? REACT_SELECT_COLORS.DARK_OPTION_SELECTED_BG
      : state.isFocused
      ? REACT_SELECT_COLORS.DARK_OPTION_FOCUSED_BG
      : REACT_SELECT_COLORS.DARK_OPTION_BG,
    "&:active": {
      backgroundColor: state.isSelected
        ? REACT_SELECT_COLORS.DARK_OPTION_SELECTED_BG
        : REACT_SELECT_COLORS.DARK_OPTION_FOCUSED_BG,
    },
  }),
  singleValue: (
    baseStyles: CSSProperties,
    { isDisabled }: ReactSelectStates
  ) => ({
    ...baseStyles,
    color: isDisabled
      ? REACT_SELECT_COLORS.DISABLED
      : REACT_SELECT_COLORS.WHITE,
    opacity: isDisabled ? 0.5 : 1,
  }),
  multiValue: (styles: CSSProperties, { isDisabled }: ReactSelectStates) => {
    return {
      ...styles,
      backgroundColor: isDisabled
        ? REACT_SELECT_COLORS.DISABLED
        : REACT_SELECT_COLORS.MULTI_VALUE_BG,
      // Fix the color inside Condition Rule's Select Options in Dark Mode
      // We will sync with the CSS of control border (border: "1px solid #3B3B4F"W)
      color: isDisabled
        ? REACT_SELECT_COLORS.DISABLED
        : REACT_SELECT_COLORS.WHITE,
      opacity: isDisabled ? 0.5 : 1,
    };
  },
  multiValueLabel: (provided: CSSProperties) => ({
    ...provided,
    backgroundColor: REACT_SELECT_COLORS.MULTI_VALUE_BG,
  }),
  input: (baseStyles: CSSProperties) => ({
    ...baseStyles,
    color: REACT_SELECT_COLORS.WHITE,
  }),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSelectStyles = (isDark: boolean): any => {
  return isDark
    ? {
        ...SELECT_DARK_STYLES,
      }
    : {
        placeholder: (base: CSSProperties) => ({
          ...base,
          opacity: 0.5,
        }),
        control: (base: CSSProperties, state: ReactSelectStates) => {
          return {
            ...base,
            borderColor: state.isFocused
              ? REACT_SELECT_COLORS.FOCUS_BORDER
              : base.borderColor,
            boxShadow: state.isFocused
              ? `0 0 0 1px ${REACT_SELECT_COLORS.FOCUS_BORDER}`
              : "none",
            "&:hover": {
              borderColor: state.isFocused
                ? REACT_SELECT_COLORS.FOCUS_BORDER
                : base.borderColor,
            },
          };
        },
        menu: (base: CSSProperties) => ({
          ...base,
          backgroundColor: REACT_SELECT_COLORS.LIGHT_MENU_BG,
          marginTop: 1,
        }),
        option: (base: CSSProperties, state: ReactSelectStates) => ({
          ...base,
          backgroundColor: state.isSelected
            ? REACT_SELECT_COLORS.LIGHT_OPTION_SELECTED_BG
            : state?.isFocused
            ? REACT_SELECT_COLORS.LIGHT_OPTION_FOCUSED_BG
            : REACT_SELECT_COLORS.LIGHT_OPTION_BG,
          color: state.isSelected
            ? REACT_SELECT_COLORS.WHITE
            : REACT_SELECT_COLORS.LIGHT_TEXT,
          "&:active": {
            backgroundColor: state.isSelected
              ? REACT_SELECT_COLORS.LIGHT_OPTION_SELECTED_BG
              : REACT_SELECT_COLORS.LIGHT_OPTION_FOCUSED_BG,
          },
        }),
        input: (base: CSSProperties) => ({
          ...base,
        }),
        multiValue: (base: CSSProperties) => ({
          ...base,
          backgroundColor: REACT_SELECT_COLORS.MULTI_VALUE_BG,
        }),
      };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFooterSelectStyles = (isDark: boolean, width = 120): any => {
  return isDark
    ? {
        ...SELECT_DARK_STYLES,
        control: (baseStyles: CSSProperties, state: ReactSelectStates) => ({
          ...SELECT_DARK_STYLES.control(baseStyles, state),
          width: width,
          maxWidth: width,
        }),
      }
    : {
        placeholder: (base: CSSProperties) => ({
          ...base,
          opacity: 0.5,
        }),
        control: (base: CSSProperties, state: ReactSelectStates) => {
          return {
            ...base,
            borderColor: state.isFocused
              ? REACT_SELECT_COLORS.FOCUS_BORDER
              : base.borderColor,
            boxShadow: state.isFocused
              ? `0 0 0 1px ${REACT_SELECT_COLORS.FOCUS_BORDER}`
              : "none",
            "&:hover": {
              borderColor: state.isFocused
                ? REACT_SELECT_COLORS.FOCUS_BORDER
                : base.borderColor,
            },
            maxWidth: width,
            width: width,
          };
        },
        menu: (base: CSSProperties) => ({
          ...base,
          backgroundColor: REACT_SELECT_COLORS.LIGHT_MENU_BG,
          marginTop: 1,
        }),
        option: (base: CSSProperties, state: ReactSelectStates) => ({
          ...base,
          backgroundColor: state?.isFocused
            ? REACT_SELECT_COLORS.LIGHT_OPTION_FOCUSED_BG
            : REACT_SELECT_COLORS.LIGHT_OPTION_BG,
          color: REACT_SELECT_COLORS.LIGHT_TEXT,
        }),
        input: (base: CSSProperties) => ({
          ...base,
        }),
      };
};

export enum TimeUnit {
  MINUTES = "MINUTES",
  HOURS = "HOURS",
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-shadow
  DAYS = "DAYS",
  MONTHS = "MONTHS",
  YEARS = "YEARS",
}

export const TimeUnitOptions: OptionTypeBase[] = [
  {
    label: "Minutes",
    value: TimeUnit.MINUTES,
  },
  {
    label: "Hours",
    value: TimeUnit.HOURS,
  },
  {
    label: "Days",
    value: TimeUnit.DAYS,
  },
];

export const getTimeUnitOptions = (
  units: TimeUnit[] = [TimeUnit.MINUTES, TimeUnit.HOURS, TimeUnit.DAYS]
): OptionTypeBase[] =>
  [
    { label: "Minutes", value: TimeUnit.MINUTES },
    { label: "Hours", value: TimeUnit.HOURS },
    { label: "Days", value: TimeUnit.DAYS },
  ].filter((option) => units.includes(option.value));

export const DayUnitOptions: OptionTypeBase[] = [
  {
    label: "Days",
    value: TimeUnit.DAYS,
  },
  {
    label: "Months",
    value: TimeUnit.MONTHS,
  },
  {
    label: "Years",
    value: TimeUnit.YEARS,
  },
];

export enum SendType {
  EMAIL = "EMAIL",
  SMS = "SMS",
}

export enum PatientPortalEventType {
  NEW_STUDY = "NEW_STUDY",
  NEW_REPORT = "NEW_REPORT",
}

export enum ViewerButtonType {
  ZOOM_IN = "ZOOM_IN",
  ZOOM_OUT = "ZOOM_OUT",
  ONT_TO_ONE = "ONT_TO_ONE",
  RESET = "RESET",
  ROTATE_LEFT = "ROTATE_LEFT",
  ROTATE_RIGHT = "ROTATE_RIGHT",
  FLIP_HORIZONTAL = "FLIP_HORIZONTAL",
  FLIP_VERTICAL = "FLIP_VERTICAL",
  NEXT = "NEXT",
  PREV = "PREV",
  TOGGLE_NEGATIVE = "TOGGLE_NEGATIVE",
}

export enum TaskStatus {
  TODO = "TODO",
  COMPLETED = "COMPLETED",
}

export const UIDValidationBehavior = {
  strict: "STRICT",
  remap: "REMAP",
};

export enum ThemeSetting {
  DARK = "DARK",
  LIGHT = "LIGHT",
  SYSTEM = "SYSTEM",
}

export enum MaskFromUnit {
  START = "START",
  CENTER = "CENTER",
  END = "END",
}

export const MaskFromUnitOptions: OptionTypeBase[] = [
  {
    label: "Start",
    value: MaskFromUnit.START,
  },
  {
    label: "Center",
    value: MaskFromUnit.CENTER,
  },
  {
    label: "End",
    value: MaskFromUnit.END,
  },
];

export enum DateComponentsUnit {
  DAY = "DAY",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export const DateComponentsUnitOptions: OptionTypeBase[] = [
  {
    label: "Day",
    value: DateComponentsUnit.DAY,
  },
  {
    label: "Month",
    value: DateComponentsUnit.MONTH,
  },
  {
    label: "Year",
    value: DateComponentsUnit.YEAR,
  },
];

export enum DefaultLoadScreens {
  STUDIES = "STUDIES",
  PATIENTS = "PATIENTS",
  ORDERS = "ORDERS",
  DOCUMENTS = "DOCUMENTS",
  WORKLIST = "WORKLIST",
}

export const ScreenOptions: OptionTypeBase[] = [
  {
    label: "Studies",
    value: DefaultLoadScreens.STUDIES,
    path: "/Studies",
    permission: PERMISSIONS.VIEW_STUDY,
  },
  {
    label: "Patients",
    value: DefaultLoadScreens.PATIENTS,
    path: "/Patients",
    permission: PERMISSIONS.VIEW_PATIENT,
  },
  {
    label: "Orders",
    value: DefaultLoadScreens.ORDERS,
    path: "/Orders",
    permission: PERMISSIONS.VIEW_ORDER,
  },
  {
    label: "Documents",
    value: DefaultLoadScreens.DOCUMENTS,
    path: "/Documents",
    permission: PERMISSIONS.VIEW_DOCUMENT,
  },
  {
    label: "Worklist",
    value: DefaultLoadScreens.WORKLIST,
    path: "/Worklist",
    permission: PERMISSIONS.ACCESS_WORKLIST,
  },
];

export enum DiagnosticReportType {
  WORKLIST = "WORKLIST",
  RADPAIR = "RADPAIR",
  IMPORTED = "IMPORTED",
}

export enum RadpairReportAction {
  START_RECORDING = "start_recording",
  STOP_RECORDING = "stop_recording",
  START_RECORDING_TRANSCRIPT = "start_recording_transcript",
  STOP_RECORDING_TRANSCRIPT = "stop_recording_transcript",
  START_RECORDING_FINAL_REPORT = "start_recording_final_report",
  STOP_RECORDING_FINAL_REPORT = "stop_recording_final_report",
  SIGN_REPORT = "sign_report",
  NEXT_FINAL_REPORT_FIELD = "next_final_report_field",
  PREV_FINAL_REPORT_FIELD = "prev_final_report_field",
  SEND_REPORT_TO_PARENT_WINDOW = "send_report_to_parent_window",
  CREATE_IMPRESSION = "create_impression",
  ADD_ADDENDUM = "add_addendum",
  DISCARD_AND_CLOSE_REPORT = "discard_and_close_report",
}

export enum RecaptchaTokenAction {
  VERIFICATION_SEND = "verification_send",
  REGISTER = "register",
  REGISTER_COMPLETED = "register_complete",
  CP_LOGIN = "login",
  CP_REGISTRATION = "cp_registration",
  PACS_LOGIN = "login",
  PATIENT_LOGIN_SEND_OTP = "send_otp",
  PATIENT_LOGIN_VERIFY_OTP = "verify_otp",
}

export const SentryFilterStatus = [404, 403];

export const LIMIT_REPORT_SIZE = 15728640; // 15mb
export const LIMIT_REPORT_MB_SIZE = 15;
// export const LIMIT_REPORT_SIZE = 2097152; // 15mb
// export const LIMIT_REPORT_MB_SIZE = 2;

export const LIMIT_CODE_MAPPING_SIZE = 1048576; // 1mb

// Page size used to fetch a dropdown's full option list in a single request
export const MAX_SIZE_QUERY = 1000;

export enum SeriesFilterRuleType {
  CLINICIAN_PORTAL = "CLINICIAN_PORTAL",
  PATIENT_PORTAL = "PATIENT_PORTAL",
}

export enum OrderPriority {
  S = "S", // Stat (do immediately)
  A = "A", // As soon as possible (a priority lower than stat)
  R = "R", // Routine
  P = "P", // Preoperative (to be done prior to surgery)
  T = "T", // Timing critical (do as near as possible to requested time)
}

export enum OrderProcedurePriority {
  STAT = "STAT",
  ASAP = "ASAP",
  URGENT = "URGENT",
  ROUTINE = "ROUTINE",
}

export enum OauthAction {
  CONNECT = "connect",
  LOGIN = "login",
}

export enum NameSegmentationPreference {
  FIRST_NAME = "FIRST_NAME",
  LAST_NAME = "LAST_NAME",
  FIRST_AND_LAST_NAME = "FIRST_AND_LAST_NAME",
}

export const MAX_WIDTH_MOBILE = 450;
export const MIN_WIDTH_TABLET = 768;
export const MAX_WIDTH_TABLET = 1024;
export const MIN_WIDTH_DESKTOP = 1025;
export const RECAPTCHA_EXECUTE_TIMEOUT_MS = 10000; // max wait for grecaptcha.execute before it counts as a failed attempt
export const RECAPTCHA_MAX_RETRIES = 3;
export const RECAPTCHA_RETRY_DELAY_MS = 1000; // base delay 1s
export const RECAPTCHA_SCRIPT_LOAD_RETRIES = 4000; // 4s wait (ms) for the reCAPTCHA script before each re-check
export enum ReportPriorityIconEnum {
  HIGHEST = "HIGHEST",
  HIGH = "HIGH",
  ROUTINE = "ROUTINE",
  MEDIUM = "MEDIUM",
  LOW = "LOW",
  LOWEST = "LOWEST",
}

export enum CustomerPACSCategory {
  RADIOLOGY = "RADIOLOGY",
  VETERINARY = "VETERINARY",
  DERMATOLOGY = "DERMATOLOGY",
  OTHER = "OTHER",
}

export enum CriticalResultStatus {
  NEW = "NEW",
  VIEWED = "VIEWED",
  ACKNOWLEDGED = "ACKNOWLEDGED",
}

export enum CriticalResultTabKey {
  MY_CASE = "MY_CASE",
  COPY_TO_ME = "COPY_TO_ME",
}

export enum ViewDiagnosticReportMode {
  STUDY = "STUDY",
  ORDER = "ORDER",
}
export enum CustomFieldObjectTypeEnum {
  PATIENT = "PATIENT",
  STUDY = "STUDY",
  ORDER = "ORDER",
}

export enum CustomFieldTypeEnum {
  TEXT = "TEXT",
  MULTIPLE_VALUE = "MULTIPLE_VALUES",
  PRIORITY = "PRIORITY",
}

export const ObjectTypeOptions: OptionTypeBase[] = [
  {
    label: "Patient",
    value: CustomFieldObjectTypeEnum.PATIENT,
  },
  {
    label: "Study",
    value: CustomFieldObjectTypeEnum.STUDY,
  },
  {
    label: "Order",
    value: CustomFieldObjectTypeEnum.ORDER,
  },
];

export const FieldTypeOptions: OptionTypeBase[] = [
  {
    label: "Text",
    value: CustomFieldTypeEnum.TEXT,
  },
  {
    label: "Priority",
    value: CustomFieldTypeEnum.PRIORITY,
  },
];

// Same height as the Small Design System badge (components/Badge)
export const WORKLIST_BADGE_HEIGHT = 22;

export const ERROR_CODE = {
  IP_FORBIDDEN: "1009",
  REQUIRE_MFA: "1010",
  UNAUTHENTICATED: "1000",
  INVALID_CSRF: "1002",
  USER_DISABLED: "1003",
  INVALID_OR_EXPIRED_TOKEN: "1002",
  INVALID_CREDENTIALS: "1004",
  INVALID_MFA: "1005",
  MALFORMED_TOKEN: "1006",
  EXPIRED_AUTHENTICATION: "1007",
  RECAPTCHA_INVALID: "1050", // RecaptchaInvalidException, e.g. BROWSER_ERROR token
};

export enum PatientClassType {
  OBSTETRICS = "B",
  COMMERCIAL_ACCOUNT = "C",
  EMERGENCY = "E",
  INPATIENT = "I",
  NOT_APPLICABLE = "N",
  OUTPATIENT = "O",
  PREADMIT = "P",
  RECURRING_PATIENT = "R",
  UNKNOWN = "U",
}

export const ADVAPACS_SUBSCRIBER_NAME_PREFIX = "advapacs-";

export enum EquivalenceType {
  EXACT = "EXACT",
  WIDER = "WIDER",
  NARROWER = "NARROWER",
}

export enum FaroFeatures {
  FHIRCAST = "FHIRCAST",
  RECAPTCHA = "RECAPTCHA",
  RADPAIR = "RADPAIR",
  DISPLAY = "DISPLAY",
}

export enum HL7ServiceType {
  GATEWAY = "GATEWAY",
  CLOUD = "CLOUD",
  HTTPS = "HTTPS",
}
export enum HL7ServiceMode {
  TCP = "TCP",
  FILE = "FILE",
}
